import { useId } from 'react';
import type { PublicUser } from '@sirocco/shared';
import { t } from '../../i18n';

/**
 * Avatar d'un membre.
 *
 * Tant qu'aucune photo n'est televersee, on affiche la silhouette grise
 * des maquettes plutot qu'une initiale : c'est ce qui donne au feed son
 * aspect uniforme.
 */
export function Avatar({
  user,
  size = 44,
  showOnline = false,
  ring = false,
}: {
  user?: Pick<PublicUser, 'username' | 'avatarUrl' | 'online'> | null;
  size?: number;
  /** Affiche la pastille verte "en ligne". */
  showOnline?: boolean;
  /** Anneau blanc, utilise sur les grands avatars poses sur l'illustration. */
  ring?: boolean;
}) {
  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        boxShadow: ring ? '0 0 0 5px var(--s-surface), var(--shadow-raised)' : undefined,
      }}
    >
      {user?.avatarUrl ? (
        <img src={user.avatarUrl} alt={user.username} width={size} height={size} />
      ) : (
        <PersonGlyph size={size} />
      )}
      {showOnline && user?.online && (
        <span
          className="avatar__online"
          style={{ width: size * 0.24, height: size * 0.24 }}
          aria-label={t.commun.enLigne}
        />
      )}
    </span>
  );
}

/**
 * Silhouette par defaut : une tete et des epaules, en gris.
 *
 * Le buste deborde volontairement du cadre pour donner l'illusion d'une
 * photo recadree ; il est donc detoure par le cercle, sans quoi ses
 * angles sortiraient de la pastille.
 */
function PersonGlyph({ size }: { size: number }) {
  // Un identifiant par instance : plusieurs avatars cohabitent sur un
  // meme ecran et un `id` duplique casserait le detourage.
  const clipId = useId();
  return (
    <svg viewBox="0 0 44 44" width={size} height={size} aria-hidden="true">
      <defs>
        <clipPath id={clipId}>
          <circle cx="22" cy="22" r="22" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`} fill="var(--s-avatar-glyph, #A9A6A2)">
        <circle cx="22" cy="17" r="6.8" />
        <path d="M22 26.4c8 0 13.8 5.2 13.8 13.4V44H8.2v-4.2C8.2 31.6 14 26.4 22 26.4Z" />
      </g>
    </svg>
  );
}

/**
 * Pile d'avatars superposes ("Participants - 9", "14 membres du serveur").
 * Au-dela de `max`, un jeton "+N" ferme la pile.
 */
export function AvatarStack({
  users,
  max = 4,
  size = 34,
  extra,
}: {
  users: PublicUser[];
  max?: number;
  size?: number;
  /** Force le nombre affiche dans le jeton final. */
  extra?: number;
}) {
  const shown = users.slice(0, max);
  const remaining = extra ?? users.length - shown.length;
  return (
    <div className="avatar-stack">
      {shown.map((user) => (
        <span key={user.id} className="avatar-stack__item" style={{ width: size, height: size }}>
          <Avatar user={user} size={size} />
        </span>
      ))}
      {remaining > 0 && (
        <span
          className="avatar-stack__item avatar-stack__more"
          style={{ width: size, height: size, fontSize: size * 0.36 }}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
}

/**
 * Icone de groupe, pour les conversations collectives.
 * Une photo de groupe la remplace des qu'il y en a une.
 */
export function GroupAvatar({
  size = 44,
  rounded = false,
  photoUrl = null,
}: {
  size?: number;
  rounded?: boolean;
  photoUrl?: string | null;
}) {
  const clipId = useId();
  const radius = rounded ? size * 0.32 : size / 2;

  if (photoUrl) {
    return (
      <span className="avatar" style={{ width: size, height: size, borderRadius: radius }}>
        <img src={photoUrl} alt="" width={size} height={size} />
      </span>
    );
  }

  return (
    <span className="avatar" style={{ width: size, height: size, borderRadius: radius }}>
      <svg viewBox="0 0 44 44" width={size} height={size} aria-hidden="true">
        <defs>
          <clipPath id={clipId}>
            <rect x="0" y="0" width="44" height="44" rx={rounded ? 14 : 22} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`} fill="var(--s-avatar-glyph, #A9A6A2)">
          <circle cx="17" cy="17.5" r="6.2" />
          <circle cx="29.8" cy="19" r="4.8" />
          <path d="M17 25.8c6.6 0 11.4 4.2 11.4 11.2V44H5.6v-7c0-7 4.8-11.2 11.4-11.2Z" />
          <path d="M29.4 25.4c5.8 0 9.6 3.4 9.6 9.4V44h-8.4v-7c0-4.4-1.4-8.2-3.8-10.8a13 13 0 0 1 2.6-.8Z" />
        </g>
      </svg>
    </span>
  );
}
